import { useEffect, useRef, useState } from 'react'

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function AnimatedNumber({
  value,
  duration = 650,
  className = 'swell-kpi-number',
  format = (current) => current.toLocaleString('en-AU'),
}: {
  value: number
  duration?: number
  className?: string
  format?: (value: number) => string
}) {
  const safeValue = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0
  const [displayValue, setDisplayValue] = useState(() => prefersReducedMotion() ? safeValue : 0)
  const displayedValue = useRef(displayValue)

  useEffect(() => {
    if (prefersReducedMotion() || typeof window.requestAnimationFrame !== 'function') {
      displayedValue.current = safeValue
      setDisplayValue(safeValue)
      return
    }

    const startValue = displayedValue.current
    const difference = safeValue - startValue
    if (!difference) return

    let frame = 0
    let startedAt: number | null = null
    const tick = (timestamp: number) => {
      startedAt ??= timestamp
      const progress = Math.min(1, (timestamp - startedAt) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      const nextValue = Math.round(startValue + difference * eased)
      displayedValue.current = nextValue
      setDisplayValue(nextValue)
      if (progress < 1) frame = window.requestAnimationFrame(tick)
    }

    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [duration, safeValue])

  return <span className={className} aria-label={format(safeValue)}>{format(displayValue)}</span>
}
